import Vue from 'vue';
import Vuex from 'vuex';

import { ConnectionService, PlanService, OrganizationService } from '../api.service';
import { AccountService } from '../api.service';

import queries from './modules/queries';
import reports from './modules/reports';
import visualizations from './modules/visualizations';
import connections from './modules/connections';

import router from '../router.js';

Vue.use(Vuex);

const store = new Vuex.Store({
    modules: {
        queries,
        reports,
        visualizations,
        connections
    },
    state: {
        organizations: [],
        exceptions: [],
        success: '',
        organizationSelected: localStorage.organizationSelected || -1,
        baseUrl: window.location.origin,
        account: [],
        pagination: {
            currentPage: 1,
            limit: 50,
            totalRows: 0
        },
        plans: [],
        users: [],
        exists: true,
        information: ''
    },
    getters: {
        getOrganizations: state => state.organizations,
        getOrganizationById: state => id => state.organizations.find(organization => organization.id === id),
        getOrganizationOwnerId: state => id => state.organizations.find(organization => organization.id === id).owner.id,
        getPlans: state => state.plans,
        getPlanById: state => id => state.plans.find(plan => plan.id === id),
        getSuccess: state => state.success,
        getExceptions: state => state.exceptions,
        getOrganizationSelected: state => state.organizationSelected,
        getAccount: state => state.account,
    },
    actions: {
        addOrganization: function({dispatch}, payload) {
            return OrganizationService.create(payload)
                .then((res) => {
                    dispatch('setSuccess', res.data.message);
                    router.replace({name : 'organization'});
                    return res;
                })
                .catch(error => {
                    dispatch('setExceptions', error.response.data);
                });
        },
        addUser: function({ dispatch, commit }, payload) {
            return OrganizationService.addUser(payload)
                .then(res => {
                    dispatch('setSuccess', res.data.message);
                    router.replace({ name: 'organization.users' });
                })
                .catch(error => {
                    if (error.response.data.message === 'Unexisting') {
                        commit('SET_EXISTS', false);
                    } else {
                        dispatch('setExceptions', error.response.data);
                    }
                });
        },
        deleteOrganization: function ({ dispatch}, payload) {
            return OrganizationService.destroy(payload.id)
                .then((res) => {
                    dispatch('setSuccess', res.data.message);
                    dispatch('fetchOrganizations');
                })
                .catch(error => {
                    dispatch('setExceptions', error.response.data);
                });
        },
        deleteUserOrganization: function ({ dispatch }, payload) {
            return OrganizationService.destroyUserOrganization(payload)
                .then((res) => {
                    dispatch('setSuccess', res.data.message);
                    dispatch('fetchUsers', payload.organizationId);
                })
                .catch((error) => {
                    dispatch('setExceptions', error.response.data);
                });
        },
        editAccount: function (context, payload) {
            context.dispatch('clearMessages');
            return AccountService.update(payload.id, payload)
                .then(res => {
                    context.commit('SET_SUCCESS', res.data.message);
                    return res;
                })
                .catch(function (error) {
                    context.commit('SET_EXCEPTIONS', error.response.data);
                });
        },
        editAccountPassword: function (context, payload) {
            context.dispatch('clearMessages');
            return AccountService.updatePassword(payload.id, payload)
                .then((res) => {
                    context.commit('SET_SUCCESS', res.data.message);
                })
                .catch(function (error) {
                    context.commit('SET_EXCEPTIONS', error.response.data);
                });
        },
        editOrganization: function (context, payload) {
            return OrganizationService.update(payload.id, payload.name, payload.plan_id)
                .then((res) => {
                    context.commit('SET_SUCCESS', res.data.message);
                    router.replace({name : 'organization'});
                })
                .catch(function (error) {
                    context.commit('SET_EXCEPTIONS', error.response.data);
                });
        },
        fetchAccount: function({ commit }) {
            return AccountService.me()
                .then(res => {
                    commit('SET_ACCOUNT', res.data.data.user);
                });
        },
        fetchOrganizations: function({ commit }) {
            return OrganizationService.query()
                .then(res => {
                    commit('SET_ORGANIZATIONS', res.data.data.organization);
                    return res;
                }).catch((error) => {
                    return error;
                });
        },
        fetchPlans: function({ commit }) {
            return PlanService.getPlans()
                .then((res) => {
                    commit('SET_PLANS', res.data.data.plans);
                }).catch((error) => {
                    return error;
                });
        },
        fetchUsers: function({ commit }, organizationId) {
            return OrganizationService.getList(organizationId)
                .then(res => {
                    commit('SET_USERS', res.data.data.users)
                });
        },
        fetchUnactiveUser: function({ commit }, payload) {
            return AccountService.getUnactiveUser(payload);
        },
        activateUser: function({ commit }, payload) {
            return AccountService.activateUser(payload);
        },
        setSuccess: function({ commit }, message) {
            commit('SET_SUCCESS', message);
            commit('CLEAR_EXCEPTIONS');
        },
        setExceptions: function({ commit }, exceptions) {
            commit('CLEAR_SUCCESS');
            commit('SET_EXCEPTIONS', exceptions);
        },
        clearExceptions: function ({commit}) {
            commit('CLEAR_EXCEPTIONS');
        },
        clearSuccess({commit}) {
            commit('CLEAR_SUCCESS');
        },
        clearMessages({commit}) {
            commit('CLEAR_EXCEPTIONS');
            commit('CLEAR_SUCCESS');
        },
        setOrganizationSelected({commit}, payload) {
            return AccountService.updateDefaultOrg(payload.id, payload);
        }
    },
    mutations: {
        setConnections: (state, connections) => {
            state.connections = connections;
        },
        SET_ORGANIZATIONS: (state, organizations) => {
            state.organizations = organizations;
        },
        SET_ORGANIZATION_SELECTED: (state, organizationSelected) => {
            window.localStorage.setItem("organizationSelected", organizationSelected);
            state.organizationSelected = organizationSelected;
        },
        SET_USERS: (state, users) => {
            state.users = users;
        },
        SET_ACCOUNT: (state, payload) => {
            state.account = payload;
        },
        SET_EXCEPTIONS: (state, message) => {
            state.exceptions.splice(0, state.exceptions.length);
            state.exceptions.push(message);
        },
        SET_PLANS: (state, plans) => {
            state.plans = plans;
        },
        SET_SUCCESS: (state, message) => {
            state.success = message;
        },
        CLEAR_EXCEPTIONS: (state) => {
            state.exceptions = [];
        },
        CLEAR_SUCCESS: (state) => {
            state.success = '';
        },
        SET_EXISTS: (state, boolean) => {
            state.exists = boolean;
        },
        SET_INFORMATION: (state, message) => {
            state.information = message;
        },
        CLEAR_INFORMATION: (state) => {
            state.information = '';
        }
    }
});
export default store;